// Projectlog (SPEC 27okt 1 en 6): één chronologische tijdlijn per project met alle gebeurtenissen
// (calculatie, prijs, status, notitie, tag), filterbaar op type en op gebruiker. Append-only: hier
// valt niets te wijzigen. Niet op de PDF voor de onderaannemer.
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CATEGORIE_LABELS, LOG_CATEGORIEEN, LOG_CATEGORIE_LABEL, type LogCategorie } from '@recotex/shared';
import { api, type ProjectLogRij } from '../lib/api';
import { fmtEUR, fmtNum, statusLabel, wieLabel } from '../lib/format';

const PER_PAGINA = 50;
const MAX_REGELS = 4;

export function ProjectLog({ sleutel }: { sleutel: string }) {
  const [categorie, setCategorie] = useState<LogCategorie | ''>('');
  const [userId, setUserId] = useState('');
  const [aantal, setAantal] = useState(PER_PAGINA);
  const q = useQuery({
    queryKey: ['projectlog', sleutel, categorie, userId],
    queryFn: () => api.dossier.log(sleutel, { categorie: categorie || undefined, userId: userId || undefined }),
    placeholderData: keepPreviousData,
  });

  // De server geeft enkel de gebruikers terug die in het gefilterde resultaat voorkomen: onthoud
  // iedereen die we al zagen, zodat de keuzelijst niet krimpt zodra er gefilterd wordt.
  const [gebruikers, setGebruikers] = useState<Map<string, string>>(() => new Map());
  useEffect(() => {
    const nieuw = q.data?.gebruikers ?? [];
    setGebruikers((prev) => {
      if (nieuw.every((g) => prev.has(g.id))) return prev;
      const m = new Map(prev);
      for (const g of nieuw) m.set(g.id, wieLabel(g.naam));
      return m;
    });
  }, [q.data]);

  const entries = q.data?.entries ?? [];
  const perDag = useMemo(() => {
    const groepen: Array<{ dag: string; regels: ProjectLogRij[] }> = [];
    for (const e of entries.slice(0, aantal)) {
      const dag = dagSleutel(e.createdAt);
      const laatste = groepen[groepen.length - 1];
      if (laatste && laatste.dag === dag) laatste.regels.push(e);
      else groepen.push({ dag, regels: [e] });
    }
    return groepen;
  }, [entries, aantal]);
  const gefilterd = !!(categorie || userId);

  return (
    <div className="card normal-case tracking-normal">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-bold">Projectlog</h3>
          <p className="text-xs text-secondary">Alle wijzigingen aan dit project, nieuwste eerst. De log kan niet gewijzigd worden.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label htmlFor="log-type" className="block text-xs font-medium text-secondary mb-1">Type</label>
            <select id="log-type" className="select py-2 min-w-[150px]" value={categorie} onChange={(e) => { setCategorie(e.target.value as LogCategorie | ''); setAantal(PER_PAGINA); }}>
              <option value="">Alle types</option>
              {LOG_CATEGORIEEN.map((c) => <option key={c} value={c}>{LOG_CATEGORIE_LABEL[c]}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="log-gebruiker" className="block text-xs font-medium text-secondary mb-1">Gebruiker</label>
            <select id="log-gebruiker" className="select py-2 min-w-[170px]" value={userId} onChange={(e) => { setUserId(e.target.value); setAantal(PER_PAGINA); }}>
              <option value="">Alle gebruikers</option>
              {[...gebruikers.entries()].sort((a, b) => a[1].localeCompare(b[1], 'nl')).map(([id, naam]) => <option key={id} value={id}>{naam}</option>)}
            </select>
          </div>
          {gefilterd && (
            <button type="button" className="btn-ghost btn-sm" onClick={() => { setCategorie(''); setUserId(''); }}>Wissen</button>
          )}
        </div>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-secondary">Laden…</p>
      ) : q.isError ? (
        <p className="text-sm text-danger">De projectlog kon niet geladen worden.</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-secondary">{gefilterd ? 'Geen gebeurtenissen voor deze filter.' : 'Nog geen gebeurtenissen op dit project.'}</p>
      ) : (
        <div className={q.isFetching ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          {perDag.map((g) => (
            <section key={g.dag} className="mb-4 last:mb-0">
              <h4 className="text-[11px] font-bold text-secondary mb-1 pb-1 border-b border-border">{dagTitel(g.regels[0]!.createdAt)}</h4>
              <ol className="list-none m-0 p-0">
                {g.regels.map((e) => <LogRegel key={e.id} entry={e} />)}
              </ol>
            </section>
          ))}
          {entries.length > aantal && (
            <button type="button" className="btn-ghost btn-sm mt-2" onClick={() => setAantal((n) => n + PER_PAGINA)}>
              Toon meer ({entries.length - aantal} oudere)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LogRegel({ entry }: { entry: ProjectLogRij }) {
  const [open, setOpen] = useState(false);
  const { titel, regels } = samenvatting(entry);
  const zichtbaar = open ? regels : regels.slice(0, MAX_REGELS);
  return (
    <li className="grid grid-cols-[3.25rem_1fr] gap-3 py-2 border-b border-lightgrey last:border-b-0 text-sm">
      <time className="text-xs text-secondary tabular-nums pt-0.5" dateTime={new Date(entry.createdAt).toISOString()} title={new Date(entry.createdAt).toLocaleString('nl-BE')}>
        {new Date(entry.createdAt).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
      </time>
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <div className="flex flex-wrap items-baseline gap-2 min-w-0">
            <span className="text-[11px] font-semibold text-secondary bg-lightgrey rounded px-1.5 py-px">
              {LOG_CATEGORIE_LABEL[entry.categorie] ?? entry.categorie}
            </span>
            <span className="font-semibold">{titel}</span>
          </div>
          <span className="text-xs text-secondary">{wieLabel(entry.userDisplay, entry.persoon)}</span>
        </div>
        {zichtbaar.length > 0 && (
          <ul className="list-none m-0 mt-0.5 p-0 text-[13px] text-primary/90 space-y-0.5">
            {zichtbaar.map((r, i) => <li key={i} className="break-words">{r}</li>)}
          </ul>
        )}
        {regels.length > MAX_REGELS && (
          <button type="button" className="text-xs text-secondary underline mt-0.5" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'minder tonen' : `nog ${regels.length - MAX_REGELS} wijziging${regels.length - MAX_REGELS === 1 ? '' : 'en'}`}
          </button>
        )}
        {entry.reden && <p className="text-xs text-secondary mt-0.5"><span className="font-semibold">Reden:</span> {entry.reden}</p>}
      </div>
    </li>
  );
}

/**
 * Compacte lijst van de laatste gebeurtenissen (dossierpaneel op de calculatie): per regel wie, wanneer
 * en wát er veranderde ("werkpost CREPI: prijs 45 → 48").
 */
export function RecenteWijzigingen({ entries, max = 5 }: { entries: ProjectLogRij[]; max?: number }) {
  if (!entries.length) return <p className="text-sm text-secondary">Nog geen gebeurtenissen.</p>;
  return (
    <ol className="list-none m-0 p-0">
      {entries.slice(0, max).map((e) => {
        const { titel, regels } = samenvatting(e);
        return (
          <li key={e.id} className="py-2 border-t border-lightgrey first:border-t-0 first:pt-0 text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-semibold min-w-0">{titel}</span>
              <time className="text-xs text-secondary tabular-nums shrink-0" dateTime={new Date(e.createdAt).toISOString()} title={new Date(e.createdAt).toLocaleString('nl-BE')}>
                {kortMoment(e.createdAt)}
              </time>
            </div>
            {regels.length > 0 && (
              <ul className="list-none m-0 mt-0.5 p-0 text-[13px] text-primary/90 space-y-0.5">
                {regels.slice(0, 3).map((r, i) => <li key={i} className="break-words">{r}</li>)}
                {regels.length > 3 && <li className="text-xs text-secondary">en nog {regels.length - 3} wijziging{regels.length - 3 === 1 ? '' : 'en'}</li>}
              </ul>
            )}
            {e.reden && <p className="text-xs text-secondary mt-0.5"><span className="font-semibold">Reden:</span> {e.reden}</p>}
            <p className="text-xs text-secondary mt-0.5">{wieLabel(e.userDisplay, e.persoon)}</p>
          </li>
        );
      })}
    </ol>
  );
}

function kortMoment(ms: number): string {
  const d = new Date(ms);
  const tijd = d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
  const vandaag = new Date();
  if (d.toDateString() === vandaag.toDateString()) return `vandaag ${tijd}`;
  const gisteren = new Date();
  gisteren.setDate(vandaag.getDate() - 1);
  if (d.toDateString() === gisteren.toDateString()) return `gisteren ${tijd}`;
  return `${d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' })} ${tijd}`;
}

// === Leesbare samenvatting van action + details ===

type Details = Record<string, unknown>;
interface Wijziging { entiteit?: string; veld?: string; label?: string; oud?: unknown; nieuw?: unknown }
interface RegelSamenvatting { soort?: string; categorie?: string; omschrijving?: string; bedrag?: number; toegerekendProject?: string; teltMee?: boolean }

const ACTIE_TITEL: Record<string, string> = {
  'project.created': 'Calculatie aangemaakt',
  'project.updated': 'Calculatie gewijzigd',
  'project.approved': 'Calculatie goedgekeurd',
  'project.deleted': 'Calculatie verwijderd',
  'project.emailed': 'Verstuurd per e-mail',
  'project.whatsapp_sent': 'Verstuurd via WhatsApp',
  'project.extra_regels': 'Extra werken en kosten gewijzigd',
  'project.toerekening': 'Toegerekend vanuit een andere calculatie',
  'project.gevel_variant': 'Gevelprijslijst gekozen',
  'project.overlap': 'Overlap beslist',
  'project.prijs': 'Prijs gewijzigd',
  'project.prijsafwijking': 'Prijsafwijking ingevoerd',
  'project.oplevering': 'Status open voor oplevering',
  'project.vrijgave': 'Posten vrijgegeven',
  'project.nieuwe_versie': 'Nieuwe versie',
  'project.conflict': 'Conflicterende wijziging',
  'project.bevestigd': 'Pop-up bevestigd',
  'project.melding': 'Melding verstuurd',
  'project.oa_gefactureerd': 'Facturatie door onderaannemer',
  'project.bouwflow_upload': 'Geplaatst in Bouwflow',
  'project.tag': 'Label',
  'project.notitie': 'Notitie',
  'tag.beheer': 'Labellijst gewijzigd',
  'historiek.geactiveerd': 'Historiek geactiveerd',
};

const OPLEVERING_LABEL: Record<string, string> = { open: 'open voor oplevering', factureerbaar: 'factureerbaar' };

export function samenvatting(e: ProjectLogRij): { titel: string; regels: string[] } {
  const d: Details = e.details ?? {};
  let titel = ACTIE_TITEL[e.action] ?? e.action;
  const regels: string[] = [];

  switch (e.action) {
    case 'project.created':
    case 'project.approved': {
      if (str(d.ploegTeam)) regels.push(`onderaannemer ${d.ploegTeam}`);
      if (num(d.totaal) != null) regels.push(`totaal ${fmtEUR(num(d.totaal))}`);
      if (num(d.openVoorOplevering)) regels.push(`waarvan ${fmtEUR(num(d.openVoorOplevering))} open voor oplevering`);
      for (const a of arr<Details>(d.prijsafwijkingen)) regels.push(`prijsafwijking ${a.post}: ${fmtEUR(num(a.standaard))} → ${fmtEUR(num(a.toegepast))}`);
      break;
    }
    case 'project.emailed':
      if (d.aanvulling) titel = 'Aanvulling verstuurd per e-mail';
      if (str(d.to)) regels.push(`naar ${d.to}`);
      if (num(d.bedrag) != null) regels.push(`bedrag ${fmtEUR(num(d.bedrag))}`);
      break;
    case 'project.whatsapp_sent':
      if (str(d.to)) regels.push(`naar ${d.to}`);
      break;
    case 'project.extra_regels':
    case 'project.toerekening': {
      if (e.action === 'project.toerekening' && str(d.vanProjectNummer)) titel = `Toegerekend vanuit ${d.vanProjectNummer}`;
      for (const r of arr<RegelSamenvatting>(d.toegevoegd)) regels.push(`toegevoegd: ${regelTekst(r)}`);
      for (const g of arr<{ voor: RegelSamenvatting; na: RegelSamenvatting }>(d.gewijzigd)) regels.push(`gewijzigd: ${regelVerschil(g.voor, g.na)}`);
      for (const r of arr<RegelSamenvatting>(d.verwijderd)) regels.push(`verwijderd: ${regelTekst(r)}`);
      for (const ef of arr<{ project: string; pctVoor: unknown; pctNa: unknown }>(d.effecten)) {
        if (num(ef.pctVoor) !== num(ef.pctNa)) regels.push(`percentage ${ef.project}: ${pct(ef.pctVoor)} → ${pct(ef.pctNa)}`);
      }
      break;
    }
    case 'project.gevel_variant': {
      const v = str(d.variantLabel) || str(d.variant);
      const delen = [v ? `variant ${v}` : null, d.afwijking ? `afwijkend van de standaard (${str(d.standaardVariant) || '—'})` : null, d.bevestigd ? 'bevestigd' : null];
      if (str(d.vorigeVariant) && d.vorigeVariant !== d.variant) delen.unshift(`van ${d.vorigeVariant}`);
      regels.push(delen.filter(Boolean).join(', '));
      break;
    }
    case 'project.prijsafwijking': {
      const verschil = num(d.verschilPerEenheid) ?? 0;
      const teken = verschil > 0 ? '+' : '';
      const pctTekst = num(d.verschilPct) != null ? `, ${teken}${fmtNum(num(d.verschilPct)!, 2)}%` : '';
      regels.push(
        `${str(d.post) || 'post'}: ${fmtEUR(num(d.standaard))} → ${fmtEUR(num(d.toegepast))} (${teken}${fmtEUR(verschil)}${pctTekst})`
        + (num(d.hoeveelheid) != null ? ` × ${fmtNum(num(d.hoeveelheid)!)}` : '')
        + (num(d.impact) != null ? `, impact ${fmtEUR(num(d.impact))}` : ''),
      );
      break;
    }
    case 'project.vrijgave': {
      regels.push(`versie ${d.versie ?? '—'}: ${fmtEUR(num(d.bedrag))} vrijgegeven`);
      for (const p of arr<{ naam?: string; bedrag?: number }>(d.posten)) regels.push(`${p.naam || 'post'} ${fmtEUR(p.bedrag)}`);
      break;
    }
    case 'project.nieuwe_versie':
      regels.push(`versie ${d.vorigeVersie ?? '—'} → ${d.nieuweVersie ?? '—'}${d.verstuurdeVersie != null ? `; de verstuurde versie ${d.verstuurdeVersie} blijft bewaard` : ''}`);
      break;
    case 'project.conflict':
      regels.push(d.uitkomst === 'geweigerd'
        ? 'wijziging geweigerd: een hoger niveau wijzigde de calculatie intussen'
        : d.uitkomst === 'overschreven' ? 'een gelijktijdige wijziging van een lager niveau werd overschreven' : str(d.uitkomst));
      break;
    case 'project.melding': {
      const ontvangers = arr<unknown>(d.ontvangers).map((o) => (typeof o === 'string' ? o : str((o as Details)?.email) || str((o as Details)?.naam))).filter(Boolean);
      if (d.soort === 'prijsafwijking') {
        titel = 'Melding prijsafwijking verstuurd';
        if (num(d.aantal) != null) regels.push(`${d.aantal} afwijking${d.aantal === 1 ? '' : 'en'}${num(d.impact) != null ? `, impact ${fmtEUR(num(d.impact))}` : ''}`);
      } else if (d.soort === 'eigen afwijking') {
        titel = d.verstuurd === false ? 'Melding eigen afwijking (mail niet verstuurd)' : 'Melding eigen afwijking verstuurd';
        for (const w of arr<unknown>(d.wat)) if (str(w)) regels.push(str(w));
      }
      if (ontvangers.length) regels.push(`naar ${ontvangers.join(', ')}`);
      break;
    }
    case 'project.oa_gefactureerd':
      titel = d.gefactureerd ? 'Gefactureerd door de onderaannemer' : 'Facturatie door de onderaannemer ongedaan gemaakt';
      break;
    case 'project.bouwflow_upload':
      // SPEC_workflow 5.1: "geplaatst in Bouwflow op datum en uur, gelukt of niet".
      titel = d.gelukt ? `Versie ${str(d.versie)} in Bouwflow geplaatst` : `Versie ${str(d.versie)} niet in Bouwflow geplaatst`;
      if (str(d.bestand)) regels.push(str(d.bestand));
      if (!d.gelukt && str(d.fout)) regels.push(str(d.fout));
      break;
    case 'project.tag':
      titel = `Label ${str(d.actie) || 'gewijzigd'}: ${str(d.tag)}`;
      break;
    case 'project.notitie': {
      titel = `Notitie ${str(d.actie) || 'gewijzigd'}`;
      if (str(d.tekst)) regels.push(`“${kort(str(d.tekst), 160)}”`);
      if (d.actie === 'bewerkt') regels.push(`“${kort(str(d.oud), 80)}” → “${kort(str(d.nieuw), 80)}”`);
      if (arr<string>(d.getagd).length) {
        const gemaild = arr<string>(d.gemaild).length;
        regels.push(`getagd: ${arr<string>(d.getagd).map((n) => `@${n}`).join(', ')}${gemaild ? ` (mail verstuurd)` : ''}`);
      }
      break;
    }
    case 'tag.beheer':
      titel = d.actie === 'hernoemd' ? `Label hernoemd: ${str(d.van)} → ${str(d.naar)}` : `${(e.entityLabel || 'Label').replace(/^Tag /, 'Label ')} ${str(d.actie)}`;
      break;
  }

  // Wijzigingen per veld (change tracking): "werkpost CREPI: prijs 45 → 48".
  for (const w of arr<Wijziging>(d.wijzigingen)) regels.push(wijzigingTekst(w));
  if (num(d.totaalVoor) != null && num(d.totaalNa) != null && num(d.totaalVoor) !== num(d.totaalNa)) {
    regels.push(`totaal ${fmtEUR(num(d.totaalVoor))} → ${fmtEUR(num(d.totaalNa))}`);
  }
  if (('pctVoor' in d || 'pctNa' in d) && num(d.pctVoor) !== num(d.pctNa)) {
    regels.push(`percentage project ${pct(d.pctVoor)} → ${pct(d.pctNa)}`);
  }
  return { titel, regels: regels.filter(Boolean) };
}

function wijzigingTekst(w: Wijziging): string {
  const wie = w.entiteit === 'calculatie' ? '' : [w.entiteit, w.label].filter(Boolean).join(' ');
  const voor = wie ? `${wie}: ` : '';
  if (w.veld === 'toegevoegd') return `${voor}toegevoegd${w.nieuw ? ` (${waarde(w.nieuw)})` : ''}`;
  if (w.veld === 'verwijderd') return `${voor}verwijderd`;
  return `${voor}${w.veld ?? 'waarde'} ${waarde(w.oud, w.veld)} → ${waarde(w.nieuw, w.veld)}`;
}

function waarde(v: unknown, veld?: string): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return fmtNum(v);
  if (typeof v === 'boolean') return v ? 'ja' : 'nee';
  if (typeof v === 'string') {
    if (veld === 'oplevering') return OPLEVERING_LABEL[v] ?? v;
    if (veld === 'status') return statusLabel(v);
    return kort(v, 60);
  }
  if (typeof v === 'object') {
    const o = v as Details;
    if ('hoeveelheid' in o || 'prijs' in o) {
      return [
        num(o.hoeveelheid) != null ? `${fmtNum(num(o.hoeveelheid)!)} ${str(o.eenheid)}`.trim() : null,
        num(o.prijs) != null ? `à ${fmtNum(num(o.prijs)!)}` : null,
      ].filter(Boolean).join(' ');
    }
    return kort(JSON.stringify(v), 60);
  }
  return String(v);
}

function regelTekst(r: RegelSamenvatting): string {
  const soort = r.soort === 'extra_werk' ? 'extra werk' : 'kost';
  const naam = r.omschrijving || (r.categorie ? CATEGORIE_LABELS[r.categorie] ?? r.categorie : '');
  return `${soort} “${kort(naam, 60)}” ${fmtEUR(r.bedrag)}`
    + (r.toegerekendProject ? ` op ${r.toegerekendProject}` : '')
    + (r.teltMee === false ? ', telt niet mee' : '');
}

function regelVerschil(a: RegelSamenvatting, b: RegelSamenvatting): string {
  const delen: string[] = [];
  if (num(a.bedrag) !== num(b.bedrag)) delen.push(`${fmtEUR(a.bedrag)} → ${fmtEUR(b.bedrag)}`);
  if ((a.toegerekendProject ?? '') !== (b.toegerekendProject ?? '')) delen.push(`toegerekend ${a.toegerekendProject || 'eigen project'} → ${b.toegerekendProject || 'eigen project'}`);
  if (a.teltMee !== b.teltMee) delen.push(b.teltMee ? 'telt nu mee' : 'telt niet meer mee');
  if ((a.omschrijving ?? '') !== (b.omschrijving ?? '')) delen.push(`omschrijving “${kort(a.omschrijving ?? '', 40)}” → “${kort(b.omschrijving ?? '', 40)}”`);
  const naam = b.omschrijving || a.omschrijving || 'regel';
  return `“${kort(naam, 60)}”${delen.length ? ': ' + delen.join(', ') : ''}`;
}

function pct(v: unknown): string {
  const n = num(v);
  return n == null ? '—' : `${fmtNum(n, 2)}%`;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

function arr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function kort(s: string, max: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
}

function dagSleutel(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dagTitel(ms: number): string {
  const d = new Date(ms);
  const vandaag = new Date();
  const gisteren = new Date();
  gisteren.setDate(vandaag.getDate() - 1);
  const zelfdeDag = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (zelfdeDag(d, vandaag)) return 'Vandaag';
  if (zelfdeDag(d, gisteren)) return 'Gisteren';
  return d.toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
